import { Button } from "@components/ui/Button"
import { ConfirmDialog } from "@components/ui/ConfirmDialog"
import { Select } from "@components/ui/Select"
import { useToast } from "@components/ui/Toaster"
import { cn } from "@lib/cn"
import { formatDate } from "@lib/intl"
import { logger } from "@lib/logger"
import { Lock, Pin, Plus, Trash2 } from "lucide-react"
import { memo, useState } from "react"
import { useTranslation } from "react-i18next"

import { useCreateNote, useDeleteNote, useUpdateNote } from "../hooks"

import { apiErrorCode, apiErrorKeys } from "@/lib/apiError"
import type { ProfileEnrollment, StudentNote } from "@/types"

interface Props {
  profileId: string
  notes: StudentNote[]
  enrollments: ProfileEnrollment[]
}

export const NoteTimeline = memo(function NoteTimeline({ profileId, notes, enrollments }: Props) {
  const { t } = useTranslation(["student-profile", "errors", "common"])
  const toast = useToast()
  const [composing, setComposing] = useState(false)
  const [body, setBody] = useState("")
  const [classroomId, setClassroomId] = useState<string>("")

  const createNote = useCreateNote(profileId)
  const updateNote = useUpdateNote(profileId)
  const deleteNote = useDeleteNote(profileId)
  // The note the professor asked to delete. Free text she typed, with no
  // undo anywhere in the product, behind a 32px icon that fired on the first
  // click (PRD-59).
  const [pendingDelete, setPendingDelete] = useState<StudentNote | null>(null)

  const surfaceError = (err: unknown, event: string) => {
    const code = apiErrorCode(err)
    toast.error(t(...apiErrorKeys(err)))
    logger.error(event, { code, profileId })
  }

  const submit = () => {
    const trimmed = body.trim()
    if (!trimmed) return
    createNote.mutate(
      { body: trimmed, classroom_id: classroomId || null },
      {
        onSuccess: () => {
          setBody("")
          setClassroomId("")
          setComposing(false)
          toast.success(t("student-profile:notes.created"))
          logger.info("student_note.created", { profileId })
        },
        onError: (err) => surfaceError(err, "student_note.create_failed"),
      },
    )
  }

  return (
    <section className="rounded-card border-border bg-surface shadow-raised border p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="text-ink text-sm font-semibold">{t("student-profile:notes.title")}</h2>
          <p className="text-ink-subtle mt-0.5 inline-flex items-center gap-1 text-xs">
            <Lock className="h-3 w-3" aria-hidden="true" />
            {t("student-profile:notes.private_hint")}
          </p>
        </div>
        {!composing && (
          <Button variant="outline" size="sm" onClick={() => setComposing(true)}>
            <Plus className="mr-1 h-3 w-3" aria-hidden="true" />
            {t("student-profile:notes.add")}
          </Button>
        )}
      </div>

      {composing && (
        <div className="rounded-card border-border mt-3 border p-3">
          <label className="text-ink-muted block text-xs font-medium">
            {t("student-profile:notes.body_label")}
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              maxLength={4000}
              placeholder={t("student-profile:notes.placeholder")}
              className="rounded-control border-border text-ink mt-1 w-full border px-3 py-2 text-sm focus:ring-2 focus:outline-none"
            />
          </label>

          {enrollments.length > 0 && (
            <div className="text-ink-muted mt-2 block text-xs font-medium">
              <span id="student-note-classroom-label" className="mb-1 block">
                {t("student-profile:notes.subject_label")}
              </span>
              {/* Default is "no subject": most observations about a student
                  aren't about one of their classes, and making the professor
                  pick one is friction that fragments the timeline. */}
              <Select
                value={classroomId}
                options={[
                  { value: "", label: t("student-profile:notes.no_subject") },
                  ...enrollments.map((e) => ({
                    value: e.classroom_id,
                    label: e.classroom_name,
                  })),
                ]}
                onChange={setClassroomId}
                ariaLabelledBy="student-note-classroom-label"
                placeholder={t("student-profile:notes.no_subject")}
                panelClassName="max-h-72 overflow-y-auto overscroll-contain"
              />
            </div>
          )}

          <div className="mt-3 flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setComposing(false)
                setBody("")
                setClassroomId("")
              }}
            >
              {t("common:actions.cancel")}
            </Button>
            <Button size="sm" onClick={submit} disabled={!body.trim() || createNote.isPending}>
              {t("student-profile:notes.save")}
            </Button>
          </div>
        </div>
      )}

      {notes.length === 0 && !composing ? (
        <p className="text-ink-subtle mt-4 text-sm">{t("student-profile:notes.empty")}</p>
      ) : (
        <ol className="mt-4 space-y-3">
          {notes.map((note) => (
            <li
              key={note.id}
              className={cn(
                "rounded-card border p-3",
                note.pinned ? "border-attention bg-attention-soft" : "border-border",
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="text-ink-subtle flex flex-wrap items-center gap-2 text-xs">
                  <time dateTime={note.created_at}>{formatDate(note.created_at)}</time>
                  {note.classroom_name && (
                    <span className="bg-inset rounded-full px-1.5 py-0.5">
                      {note.classroom_name}
                    </span>
                  )}
                </div>
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    aria-label={
                      note.pinned
                        ? t("student-profile:notes.unpin")
                        : t("student-profile:notes.pin")
                    }
                    onClick={() =>
                      updateNote.mutate(
                        { noteId: note.id, data: { pinned: !note.pinned } },
                        { onError: (err) => surfaceError(err, "student_note.update_failed") },
                      )
                    }
                    className={cn(
                      "rounded-control flex h-11 w-11 items-center justify-center focus:ring-2 focus:outline-none md:h-8 md:w-8",
                      note.pinned ? "text-attention-ink" : "text-ink-subtle hover:text-ink-muted",
                    )}
                  >
                    <Pin className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    aria-label={t("student-profile:notes.delete")}
                    onClick={() => setPendingDelete(note)}
                    className="rounded-control text-ink-subtle hover:text-danger-ink flex h-11 w-11 items-center justify-center md:h-8 md:w-8"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </div>
              </div>
              <p className="text-ink-muted mt-1.5 text-sm whitespace-pre-wrap">{note.body}</p>
            </li>
          ))}
        </ol>
      )}

      {/* A note is free text she typed and there is no undo anywhere in this
          product, so the delete pauses (PRD-59). The excerpt is what makes
          the dialog answerable: on a timeline of eight observations, "¿Borrar
          esta nota?" alone does not say which one. */}
      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          const note = pendingDelete
          if (!note) return
          setPendingDelete(null)
          deleteNote.mutate(note.id, {
            onSuccess: () => toast.success(t("student-profile:notes.deleted")),
            onError: (err) => surfaceError(err, "student_note.delete_failed"),
          })
        }}
        loading={deleteNote.isPending}
        title={t("student-profile:notes.confirm_delete.title")}
        message={t("student-profile:notes.confirm_delete.message", {
          excerpt: excerpt(pendingDelete?.body ?? ""),
        })}
        consequences={t("student-profile:notes.confirm_delete.consequences")}
        confirmLabel={t("student-profile:notes.confirm_delete.confirm")}
        cancelLabel={t("student-profile:notes.confirm_delete.cancel")}
        closeLabel={t("common:actions.close")}
      />
    </section>
  )
})

/** First words of a note, enough to recognise it in the dialog. */
const EXCERPT_CHARS = 80
const excerpt = (body: string) => {
  const flat = body.trim().replace(/\s+/g, " ")
  return flat.length > EXCERPT_CHARS ? `${flat.slice(0, EXCERPT_CHARS).trimEnd()}…` : flat
}
